import React, { useState, useEffect, useCallback } from 'react';
import { getPrescriptionSuggestions } from '../../../lib/prescriptionSuggestions';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage } from '../../../context/LanguageContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import {
  ClipboardList, Dumbbell, Wind, ShieldCheck, Heart, CheckCircle, Clock, XCircle,
  CalendarDays, History, ChevronRight, Loader2, AlertTriangle, MinusCircle, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

const CAT_COLORS = {
  STRENGTH: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  AEROBIC: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  RTP: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  RECOVERY: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  PLYOMETRIC: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  MIXED: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
};
const CAT_ICONS = { STRENGTH: Dumbbell, AEROBIC: Wind, RTP: ShieldCheck, RECOVERY: Heart, PLYOMETRIC: ClipboardList, MIXED: ClipboardList };
const STATUS_STYLES = {
  ASSIGNED: { icon: Clock, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20', label: 'Assigned' },
  IN_PROGRESS: { icon: Clock, color: 'text-amber-400 bg-amber-500/10 border-amber-500/20', label: 'In Progress' },
  COMPLETED: { icon: CheckCircle, color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', label: 'Completed' },
  PARTIAL: { icon: MinusCircle, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20', label: 'Partial' },
  SKIPPED: { icon: XCircle, color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20', label: 'Skipped' },
  MISSED: { icon: XCircle, color: 'text-rose-400 bg-rose-500/10 border-rose-500/20', label: 'Missed' },
};

export default function AthletePrescriptionsPage() {
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('today');
  const [statusDialog, setStatusDialog] = useState(null); // { assignment, targetStatus }
  const [feedbackNote, setFeedbackNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedRx, setExpandedRx] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/prescriptions/my-prescriptions');
      setAssignments(res.data);
    } catch (e) {
      toast.error('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusUpdate = async () => {
    if (!statusDialog) return;
    setSubmitting(true);
    try {
      await api.put(`/prescriptions/assignments/${statusDialog.assignment.id}`, {
        status: statusDialog.targetStatus,
        feedback_note: feedbackNote || null,
      });
      toast.success(`Marked as ${STATUS_STYLES[statusDialog.targetStatus]?.label || statusDialog.targetStatus}`);
      setStatusDialog(null);
      setFeedbackNote('');
      fetchData();
    } catch (e) {
      toast.error('Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const openStatusDialog = (assignment, targetStatus) => {
    setStatusDialog({ assignment, targetStatus });
    setFeedbackNote('');
  };

  const today = new Date().toISOString().split('T')[0];

  // Compute the actual date for each assignment
  const getAssignmentDate = (a) => {
    // For microcycle per-day: derive date from week_start + day_of_week
    if (a.day_of_week != null && a.prescription?.week_start) {
      const ws = new Date(a.prescription.week_start + 'T00:00:00');
      ws.setDate(ws.getDate() + a.day_of_week);
      return ws.toISOString().split('T')[0];
    }
    // For single session: use prescription date
    return a.prescription?.date;
  };

  const todayAssignments = assignments.filter(a => {
    const aDate = getAssignmentDate(a);
    if (['COMPLETED', 'SKIPPED', 'MISSED'].includes(a.status)) return false;
    // Today + any past-due (overdue) ASSIGNED/IN_PROGRESS so they don't get orphaned
    return aDate && aDate <= today;
  });
  const upcomingAssignments = assignments.filter(a => {
    const aDate = getAssignmentDate(a);
    return !['COMPLETED', 'SKIPPED', 'MISSED'].includes(a.status) && aDate && aDate > today;
  });
  const unscheduledAssignments = assignments.filter(a => {
    const aDate = getAssignmentDate(a);
    return !['COMPLETED', 'SKIPPED', 'MISSED'].includes(a.status) && !aDate;
  });
  const historyAssignments = assignments.filter(a => ['COMPLETED', 'SKIPPED', 'MISSED', 'PARTIAL'].includes(a.status));

  // Helper: find athlete-specific data for the current user from prescription's athlete_data
  const getMyAthleteData = (rx, block, exercise) => {
    const athleteData = rx?.athlete_data || {};
    const athleteId = user?.athlete_id;
    if (!athleteId) return null;

    return getPrescriptionSuggestions(athleteData, block, exercise).find(s => s.athlete_id === athleteId) || null;
  };

  // Helper: find athlete's jump data from prescription's athlete_data
  const getMyJumpData = (rx, block) => {
    if (block.block_type !== 'PLYOMETRIC' || !block.jump_focus) return null;
    const athleteData = rx?.athlete_data || {};
    const athleteId = user?.athlete_id;
    if (!athleteId) return null;
    const jumpKey = `jump_${block.jump_focus}`;
    const jumpData = athleteData[jumpKey];
    if (!jumpData || !jumpData[athleteId]) return null;
    return jumpData[athleteId];
  };

  const renderAssignment = (a, showActions = true) => {
    const rx = a.prescription;
    if (!rx) return null;
    const Icon = CAT_ICONS[rx.category] || ClipboardList;
    const isExpanded = expandedRx === a.id;
    const isMicrocycle = rx.prescription_type === 'MICROCYCLE';
    // For microcycle per-day assignments, show only that day's blocks
    const dayOfWeek = a.day_of_week;
    const dayLabel = a.day_label;
    const allBlocks = isMicrocycle && dayOfWeek != null
      ? ((rx.microcycle_days || []).find(d => d.day_of_week === dayOfWeek)?.blocks || [])
      : isMicrocycle
        ? (rx.microcycle_days || []).flatMap(d => d.blocks || [])
        : (rx.blocks || []);
    const statusStyle = STATUS_STYLES[a.status] || STATUS_STYLES.ASSIGNED;
    const StatusIcon = statusStyle.icon;

    // Compute actual date for microcycle per-day assignments
    const assignmentDate = getAssignmentDate(a);
    const formattedDate = assignmentDate
      ? new Date(assignmentDate + 'T00:00:00').toLocaleDateString(
          language === 'IT' ? 'it-IT' : 'en-GB',
          { weekday: 'long', day: 'numeric', month: 'long' }
        )
      : null;

    return (
      <Card key={a.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`athlete-assignment-${a.id}`}>
        <CardContent className="p-0">
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/20 transition-colors"
            onClick={() => setExpandedRx(isExpanded ? null : a.id)}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-sm ${CAT_COLORS[rx.category]}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-zinc-100">
                  {isMicrocycle && dayLabel
                    ? `${formattedDate ? formattedDate + ' - ' : (dayLabel + ' - ')}${rx.title}`
                    : rx.title}
                </h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className={`text-[10px] ${CAT_COLORS[rx.category]}`}>{rx.category}</Badge>
                  {!isMicrocycle && rx.date && <span className="text-[11px] text-zinc-500">{rx.date}</span>}
                  {isMicrocycle && formattedDate && (
                    <span className="text-[11px] text-zinc-500">{assignmentDate}</span>
                  )}
                  {isMicrocycle && dayLabel && <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400">Microcycle - {dayLabel}</Badge>}
                  {isMicrocycle && !dayLabel && <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">Microcycle</Badge>}
                  <Badge variant="outline" className={`text-[10px] ${statusStyle.color}`}>
                    <StatusIcon className="w-3 h-3 mr-0.5" />{statusStyle.label}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {showActions && a.status === 'ASSIGNED' && (
                <div className="flex gap-1.5 flex-wrap" onClick={e => e.stopPropagation()}>
                  <Button size="sm" onClick={() => openStatusDialog(a, 'IN_PROGRESS')}
                    className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 border border-amber-500/30 text-xs h-7 px-2"
                    data-testid={`in-progress-btn-${a.id}`}>
                    <Clock className="w-3 h-3 mr-1" /> In Progress
                  </Button>
                  <Button size="sm" onClick={() => openStatusDialog(a, 'COMPLETED')}
                    className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30 text-xs h-7 px-2"
                    data-testid={`complete-btn-${a.id}`}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Complete
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openStatusDialog(a, 'PARTIAL')}
                    className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 text-xs h-7 px-2"
                    data-testid={`partial-btn-${a.id}`}>
                    <MinusCircle className="w-3 h-3 mr-1" /> Partial
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openStatusDialog(a, 'MISSED')}
                    className="border-zinc-600 text-zinc-400 hover:bg-zinc-800 text-xs h-7 px-2"
                    data-testid={`missed-btn-${a.id}`}>
                    <XCircle className="w-3 h-3 mr-1" /> Missed
                  </Button>
                </div>
              )}
              {showActions && a.status === 'IN_PROGRESS' && (
                <div className="flex gap-1.5 flex-wrap" onClick={e => e.stopPropagation()}>
                  <Button size="sm" onClick={() => openStatusDialog(a, 'COMPLETED')}
                    className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30 text-xs h-7 px-2"
                    data-testid={`complete-btn-${a.id}`}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Complete
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openStatusDialog(a, 'PARTIAL')}
                    className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 text-xs h-7 px-2"
                    data-testid={`partial-btn-${a.id}`}>
                    <MinusCircle className="w-3 h-3 mr-1" /> Partial
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openStatusDialog(a, 'MISSED')}
                    className="border-zinc-600 text-zinc-400 hover:bg-zinc-800 text-xs h-7 px-2"
                    data-testid={`missed-btn-${a.id}`}>
                    <XCircle className="w-3 h-3 mr-1" /> Missed
                  </Button>
                </div>
              )}
              {showActions && a.status === 'PARTIAL' && (
                <div className="flex gap-1.5" onClick={e => e.stopPropagation()}>
                  <Button size="sm" onClick={() => openStatusDialog(a, 'COMPLETED')}
                    className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 border border-emerald-500/30 text-xs h-7 px-2"
                    data-testid={`complete-partial-btn-${a.id}`}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Complete
                  </Button>
                </div>
              )}
              <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            </div>
          </div>

          {isExpanded && (
            <div className="border-t border-zinc-800 p-4 space-y-3">
              {rx.notes && (
                <div className="text-xs text-zinc-400 bg-zinc-800/30 p-2 rounded-sm">{rx.notes}</div>
              )}
              {allBlocks.map((block, bi) => (
                <div key={bi} className={`border rounded-sm p-3 ${CAT_COLORS[block.block_type] || 'border-zinc-700'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-zinc-200">{block.name}</span>
                    <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">{block.block_type}</Badge>
                    {block.training_objective && <Badge className="text-[10px] bg-zinc-700/50 text-zinc-300">{block.training_objective.replace(/_/g, ' ')}</Badge>}
                    {block.jump_focus && <Badge className="text-[10px] bg-orange-500/15 text-orange-400">{block.jump_focus.replace(/_/g, ' ')}</Badge>}
                  </div>
                  <div className="space-y-1">
                    {(block.exercises || []).map((ex, ei) => {
                      const myData = getMyAthleteData(rx, block, ex);
                      return (
                        <div key={ei}>
                          <div className="flex items-center justify-between text-xs bg-zinc-900/40 rounded px-2 py-1.5">
                            <span className="text-zinc-200">{ex.exercise_name}</span>
                            <div className="flex items-center gap-2 text-zinc-400">
                              {ex.sets && <span>{ex.sets}x{ex.reps || '-'}</span>}
                              {ex.load_mode === 'PCT_BW' && ex.load_pct_bw != null ? (
                                ex.resolved_load_kg != null ? (
                                  <span className="text-emerald-400" data-testid={`bw-load-${bi}-${ei}`}>{ex.resolved_load_kg}kg <span className="text-zinc-500">({ex.load_pct_bw}%BW)</span></span>
                                ) : ex.bw_missing ? (
                                  <span className="text-amber-400" data-testid={`bw-missing-${bi}-${ei}`}>{language === 'IT' ? 'Peso non disponibile' : 'Weight unavailable'}</span>
                                ) : (
                                  <span className="text-zinc-400">{ex.load_pct_bw}%BW</span>
                                )
                              ) : ex.load_kg ? (
                                <span className="text-emerald-400">{ex.load_kg}kg</span>
                              ) : null}
                              {ex.velocity_target && <span className="text-blue-400">{ex.velocity_target}m/s</span>}
                              {ex.work_sec && <span>{ex.work_sec}s/{ex.rest_sec}s</span>}
                              {ex.intensity_pct && <span>{ex.intensity_pct}%</span>}
                              {ex.duration_sec && <span>{Math.round(ex.duration_sec / 60)}min</span>}
                              {ex.distance_m && <span>{ex.distance_m}m</span>}
                              {ex.target_speed_kmh && <span>{ex.target_speed_kmh}km/h</span>}
                              {ex.contacts && <span>{ex.contacts} contacts</span>}
                              {ex.intensity_level && <span className="text-orange-400">{ex.intensity_level}</span>}
                            </div>
                          </div>
                          {/* My personalized values */}
                          {myData && !myData.warning && (
                            <div className="ml-2 mt-1 mb-1 bg-amber-500/5 border border-amber-500/20 rounded px-2 py-1.5" data-testid={`my-values-${bi}-${ei}`}>
                              <div className="flex items-center gap-1 mb-1">
                                <Sparkles className="w-3 h-3 text-amber-400" />
                                <span className="text-[10px] font-semibold text-amber-400">
                                  {language === 'IT' ? 'I tuoi valori' : 'Your Values'}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 text-[11px] flex-wrap">
                                {myData.mas_kmh != null && <span className="text-zinc-400">MAS: <span className="text-zinc-200">{myData.mas_kmh} km/h</span></span>}
                                {myData.target_distance_m != null && <span className="text-emerald-400 font-medium">{myData.target_distance_m}m/rep</span>}
                                {myData.total_distance_m != null && <span className="text-blue-400 font-medium">{myData.total_distance_m}m total</span>}
                                {myData.target_speed_kmh != null && <span className="text-emerald-400">{myData.target_speed_kmh} km/h</span>}
                                {myData.suggested_load_min_kg != null && (
                                  <span className="text-emerald-400 font-medium">{myData.suggested_load_min_kg}-{myData.suggested_load_max_kg} kg</span>
                                )}
                                {myData.suggested_velocity_min != null && (
                                  <span className="text-blue-400">{myData.suggested_velocity_min}-{myData.suggested_velocity_max} m/s</span>
                                )}
                                {myData.estimated_1rm != null && <span className="text-zinc-500">e1RM: {myData.estimated_1rm}kg</span>}
                              </div>
                            </div>
                          )}
                          {myData?.warning && (
                            <div className="ml-2 mt-1 mb-1 flex items-center gap-1 text-[11px] text-amber-400">
                              <AlertTriangle className="w-3 h-3" /> {myData.warning}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Jump / Plyometric personalized data */}
                  {(() => {
                    const myJump = getMyJumpData(rx, block);
                    if (!myJump) return null;
                    return (
                      <div className="mt-2 bg-orange-500/5 border border-orange-500/20 rounded px-2 py-1.5" data-testid={`my-jump-data-${bi}`}>
                        <div className="flex items-center gap-1 mb-1">
                          <Sparkles className="w-3 h-3 text-orange-400" />
                          <span className="text-[10px] font-semibold text-orange-400">
                            {language === 'IT' ? 'I tuoi dati Jump' : 'Your Jump Data'}
                          </span>
                          {(myJump.flags || []).includes('IN_RTP') && (
                            <Badge className="text-[8px] bg-rose-500/20 text-rose-400 ml-1">RTP</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-[11px] flex-wrap">
                          {myJump.metrics?.cmj_height_cm != null && <span className="text-zinc-400">CMJ: <span className="text-zinc-200">{myJump.metrics.cmj_height_cm}cm</span></span>}
                          {myJump.metrics?.sj_height_cm != null && <span className="text-zinc-400">SJ: <span className="text-zinc-200">{myJump.metrics.sj_height_cm}cm</span></span>}
                          {myJump.metrics?.dj_rsi != null && <span className="text-zinc-400">RSI: <span className="text-zinc-200">{Number(myJump.metrics.dj_rsi).toFixed(2)}</span></span>}
                          {myJump.metrics?.asymmetry_pct != null && <span className="text-zinc-400">Asym: <span className="text-zinc-200">{myJump.metrics.asymmetry_pct}%</span></span>}
                        </div>
                        {myJump.prescribed_values && (
                          <div className="flex items-center gap-3 text-[11px] mt-1">
                            <span className="text-orange-400 font-medium">{language === 'IT' ? 'Prescritto:' : 'Your Rx:'}</span>
                            {myJump.prescribed_values.sets && <span className="text-zinc-200">{myJump.prescribed_values.sets} sets</span>}
                            {myJump.prescribed_values.reps && <span className="text-zinc-200">{myJump.prescribed_values.reps} reps</span>}
                            {myJump.prescribed_values.contacts && <span className="text-zinc-200">{myJump.prescribed_values.contacts} contacts</span>}
                            {myJump.prescribed_values.intensity_level && <span className="text-zinc-200">{myJump.prescribed_values.intensity_level}</span>}
                          </div>
                        )}
                        {myJump.warning && (
                          <div className="flex items-center gap-1 mt-1 text-amber-400 text-[10px]">
                            <AlertTriangle className="w-3 h-3" /> {myJump.warning}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
              {a.feedback_note && (
                <div className="text-xs text-zinc-400 bg-zinc-800/30 p-2 rounded-sm">
                  <span className="text-zinc-500">{language === 'IT' ? 'Feedback:' : 'Feedback:'}</span> {a.feedback_note}
                </div>
              )}
              {a.completed_at && (
                <div className="text-[11px] text-zinc-500">
                  {language === 'IT' ? 'Completato:' : 'Completed:'} {new Date(a.completed_at).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="athlete-prescriptions-page">
      <div>
        <h1 className="text-2xl font-bold text-zinc-100" data-testid="athlete-rx-title">
          {language === 'IT' ? 'Prescrizioni' : 'My Prescriptions'}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {language === 'IT' ? 'Visualizza e completa le tue prescrizioni' : 'View and complete your training prescriptions'}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="today" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400" data-testid="tab-today">
            <CalendarDays className="w-4 h-4 mr-1.5" />
            {language === 'IT' ? 'Oggi' : 'Today'}
            {todayAssignments.length > 0 && (
              <Badge className="ml-1.5 bg-amber-500/20 text-amber-400 text-[10px]">{todayAssignments.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400" data-testid="tab-upcoming">
            <Clock className="w-4 h-4 mr-1.5" />
            {language === 'IT' ? 'Prossimi' : 'Upcoming'}
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-zinc-700/50 data-[state=active]:text-zinc-300" data-testid="tab-history">
            <History className="w-4 h-4 mr-1.5" />
            {language === 'IT' ? 'Storico' : 'History'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="mt-4">
          {loading ? (
            <div className="flex justify-center py-8"><div className="text-zinc-500">Loading...</div></div>
          ) : (todayAssignments.length === 0 && unscheduledAssignments.length === 0) ? (
            <div className="text-center py-12">
              <CheckCircle className="w-10 h-10 text-emerald-500/30 mx-auto mb-2" />
              <p className="text-zinc-400 font-medium">{language === 'IT' ? 'Nessuna prescrizione per oggi' : 'No prescriptions for today'}</p>
              <p className="text-xs text-zinc-600 mt-1">{language === 'IT' ? 'Sei a posto!' : "You're all caught up!"}</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="today-list">
              {todayAssignments.map(a => renderAssignment(a, true))}
              {unscheduledAssignments.map(a => renderAssignment(a, true))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="upcoming" className="mt-4">
          {upcomingAssignments.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400">{language === 'IT' ? 'Nessuna prescrizione imminente' : 'No upcoming prescriptions'}</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="upcoming-list">
              {upcomingAssignments.map(a => renderAssignment(a, true))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {historyAssignments.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
              <p className="text-zinc-400">{language === 'IT' ? 'Nessuna prescrizione completata' : 'No completed prescriptions yet'}</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="history-list">
              {historyAssignments.map(a => renderAssignment(a, false))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Status Update Dialog */}
      <Dialog open={!!statusDialog} onOpenChange={(open) => { if (!open) { setStatusDialog(null); setFeedbackNote(''); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">
              {statusDialog?.targetStatus === 'COMPLETED' && (language === 'IT' ? 'Completa Prescrizione' : 'Complete Prescription')}
              {statusDialog?.targetStatus === 'IN_PROGRESS' && (language === 'IT' ? 'In Corso' : 'Mark as In Progress')}
              {statusDialog?.targetStatus === 'PARTIAL' && (language === 'IT' ? 'Parzialmente Completata' : 'Mark as Partial')}
              {statusDialog?.targetStatus === 'MISSED' && (language === 'IT' ? 'Segna come Saltata' : 'Mark as Missed')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">
              {statusDialog?.targetStatus === 'COMPLETED' && (language === 'IT' ? 'Come ti sei sentito?' : 'How did it go? Leave a short note (optional).')}
              {statusDialog?.targetStatus === 'PARTIAL' && (language === 'IT' ? 'Cosa sei riuscito a completare?' : 'What did you manage to complete? (optional)')}
              {statusDialog?.targetStatus === 'MISSED' && (language === 'IT' ? 'Perche\' non hai potuto completare?' : 'Why couldn\'t you complete it? (optional)')}
            </p>
            <Textarea
              value={feedbackNote}
              onChange={e => setFeedbackNote(e.target.value)}
              placeholder={language === 'IT' ? 'Note...' : 'Notes...'}
              className="bg-zinc-800 border-zinc-700 text-zinc-100"
              rows={3}
              data-testid="feedback-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setStatusDialog(null); setFeedbackNote(''); }} className="border-zinc-700 text-zinc-400" data-testid="cancel-status-btn">
              {language === 'IT' ? 'Annulla' : 'Cancel'}
            </Button>
            <Button onClick={handleStatusUpdate} disabled={submitting}
              className={
                statusDialog?.targetStatus === 'COMPLETED' ? 'bg-emerald-500 hover:bg-emerald-600 text-black' :
                statusDialog?.targetStatus === 'PARTIAL' ? 'bg-orange-500 hover:bg-orange-600 text-black' :
                'bg-zinc-600 hover:bg-zinc-500 text-white'
              }
              data-testid="confirm-status-btn">
              {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : (
                statusDialog?.targetStatus === 'COMPLETED' ? <CheckCircle className="w-4 h-4 mr-1" /> :
                statusDialog?.targetStatus === 'PARTIAL' ? <MinusCircle className="w-4 h-4 mr-1" /> :
                <XCircle className="w-4 h-4 mr-1" />
              )}
              {language === 'IT' ? 'Conferma' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
